import { logError } from '@databases/logs';

/**
 * Runs the given AP callback every 100 ms until its resolve function is called.
 * This acts as a workaround for a bug in the AP bridge where calling a function (such as AP.navigator.getLocation(callback))
 * too early will result in an exception on the host, but inside the iframe the callback is simply never called. This
 * method will keep calling the function until the callback is finally called.
 */
// Exported for tests
export function retryConnectCallback<T>(
  callback: (resolve: (value: T) => void) => void
): Promise<T> {
  return new Promise<T>((resolve) => {
    const runCallback = () => {
      callback((value) => {
        if (interval) {
          clearInterval(interval);
        }
        resolve(value);
      });
    };

    const interval = setInterval(runCallback, 100);
    runCallback();
  });
}

/**
 * Returns the current space key.
 */
export function getCurrentSpace(): { key: string } {
  const queryParams = new URLSearchParams(window.location.search);
  const spaceKey = queryParams.get('spaceKey');
  if (!spaceKey) {
    const error = new Error('URL does not have space key');
    logError(error);
    throw error;
  }
  return { key: spaceKey };
}

/**
 * Return the top window
 */
export function getWindow(): Window {
  try {
    return window.location.toString() != window.parent.location.toString()
      ? window.parent
      : window;
  } catch {
    // If we're in an iframe and the parent is on a different domain, we can't access it.
    // In this case we fall back to the current window. This covers the case where the app is
    // embedded in a different domain (e.g. in a different tenant) via smartlink.
    return window;
  }
}
